import { useEffect, useMemo, useState } from 'react';
import { isNotNullish, isNullish } from '@chainflip/utils/guard';
import { subDays } from 'date-fns';
import { motion, AnimatePresence } from 'motion/react';
import Button from '@/shared/components/flip-ui-kit/Button';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import PageTab from '@/shared/components/PageTab';
import { StatCard } from '@/shared/components/StatCard';
import { TabSelect } from '@/shared/components/TabSelect';
import { TimeframeTabSelect } from '@/shared/components/TimeframeTabSelect';
import useBoolean from '@/shared/hooks/useBoolean';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useQueryParam from '@/shared/hooks/useQueryParam';
import useTimeframeSelection from '@/shared/hooks/useTimeframeSelection';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import CoinsIcon from '@/shared/icons/flip-ui-kit/large/Coins';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/large/PlusCircle';
import Users from '@/shared/icons/flip-ui-kit/large/Users';
import MoneyBagIcon from '@/shared/icons/flip-ui-kit/small/MoneyBag';
import PercentageIcon from '@/shared/icons/flip-ui-kit/small/Percentage';
import VolumeIcon from '@/shared/icons/flip-ui-kit/small/Volume';
import { defaultAnimationProps } from '@/shared/utils/consts';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import { isChainflipSs58Address } from '@/shared/utils/helpers';
import { formatUsdValue } from '@/shared/utils/numbers';
import { formatPct } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';
import ActivityView from '../../components/v2/delegate/ActivityView';
import {
  BalanceChart,
  EmptyBalanceChartPlaceholder,
} from '../../components/v2/delegate/BalanceChart';
import DelegateFlipCard from '../../components/v2/delegate/DelegateFlipCard';
import DelegationFlow from '../../components/v2/delegate/DelegationFlow';
import { RewardsChart } from '../../components/v2/delegate/RewardsChart';
import { Layout } from '../../components/v2/Layout';
import { checkForActivityQuery } from '../../graphql/explorer/delegatorQueries';
import { GetDelegationRewardsSinceDaysAgoQuery } from '../../graphql/explorer/rewards';
import { useDelegationSnapshotData } from '../../hooks/useDelegationSnapshotData';
import useOperatorsAndAuction, { type OperatorSummary } from '../../hooks/useOperatorsAndAuction';

const chartTabs = [
  {
    label: 'Balance',
  },
  {
    label: 'Rewards',
  },
];
type ChartTabOption = (typeof chartTabs)[number];

const DelegatedView = ({
  delegatorIdSs58,
  operators,
  currentOperator,
  upcomingOperator,
  queryParamOperator,
}: {
  delegatorIdSs58: `cF${string}`;
  operators: OperatorSummary[];
  currentOperator: OperatorSummary | undefined;
  upcomingOperator: OperatorSummary | undefined;
  queryParamOperator: OperatorSummary | undefined;
}) => {
  const [selectedTab, setSelectedTab] = useQueryParam({
    name: 'tab',
    defaultValue: 'my-position',
    parse: (param: string) => (param === 'my-position' ? 'my-position' : 'activity'),
  });
  const [selectedOperator, setSelectedOperator] = useState(queryParamOperator);
  const { value: open, setTrue: openModal, setFalse: closeModal } = useBoolean(false);
  const initialDate = useMemo(() => new Date(), []);

  const [selectedChartTab, setSelectedChartTab] = useState(chartTabs[0]);

  const {
    selectedTimeframe,
    setSelectedTimeframe,
    chartStartDate,
    chartEndDate,
    timeframeTabOptions,
  } = useTimeframeSelection({ initialTimeframe: '1M', allTimeStartDate: 'firstDelegation' });

  const { data: delegation, isLoading: isRewardsLoading } = useGqlQuery(
    GetDelegationRewardsSinceDaysAgoQuery,
    {
      variables: {
        delegationIdSs58: delegatorIdSs58,
        startDate: subDays(initialDate, selectedTimeframe.value).toISOString(),
      },
      context: { clientName: 'processor' },
    },
  );
  const { price: flipPrice } = useTokenPrice(chainflipAssetMap.Flip);

  const rewards = TokenAmount.fromAsset(delegation?.rewards?.aggregates?.sum?.amount ?? 0, 'Flip');
  const rewardsValueUsd = delegation?.rewards?.aggregates?.sum?.valueUsd ?? 0;

  const { currentEpoch, isLoading: isApyLoading } = useEvmScAccount(true);
  const delegatorApy = currentOperator?.currentEpoch.delegatorApy;

  const { data, oldestSnapshot, isLoading, exportCsvData } = useDelegationSnapshotData(
    delegatorIdSs58,
    chartStartDate,
    chartEndDate,
    'D',
  );
  const showEmptyState = isNullish(oldestSnapshot) && !isLoading;

  const currentDelegatedAmount = currentEpoch.delegatedBalance;
  const currentDelegatedValueUsd = currentEpoch.delegatedBalance.toUsdNumber(flipPrice);

  return (
    <>
      <div className="flex items-center justify-between">
        <span className="font-aeonikMedium text-24 text-white">Delegate</span>
        <Button
          size="medium"
          type="primary-standard"
          icon={<PlusCircleIcon className="mr-1 active:text-black" />}
          iconPos="left"
          onClick={() =>
            window.open(
              new URL('/?srcAsset=Usdc&destAsset=Flip', process.env.NEXT_PUBLIC_SWAPPING_URL),
              '_blank',
            )
          }
        >
          Buy {FLIP_SYMBOL}
        </Button>
      </div>
      <div className="flex w-full flex-col-reverse gap-y-6 md:flex-col">
        <PageTab
          className="order-1 md:order-none"
          selectedTab={selectedTab}
          onSelect={setSelectedTab}
          options={[
            {
              Icon: Users,
              iconClassName: 'group-data-active:text-cf-green-1',
              title: 'My Position',
              value: 'my-position',
            },
            {
              Icon: CoinsIcon,
              iconClassName: 'group-data-active:text-cf-green-1',
              title: 'Balances & Activity',
              value: 'activity',
            },
          ]}
        />

        {selectedTab === 'my-position' ? (
          <div className="flex flex-col-reverse gap-6 md:flex-row">
            {!data || showEmptyState ? (
              <div className="h-[352px] w-full">
                <EmptyBalanceChartPlaceholder />
              </div>
            ) : (
              <div className="flex w-full flex-col rounded-md border border-cf-gray-4 bg-cf-gray-3 font-aeonikMedium text-16 text-cf-white">
                <div className="flex flex-col justify-between gap-y-2 p-6 min-[500px]:flex-row">
                  <div className="font-aeonikMedium text-16 text-cf-white">Delegation Stats</div>
                  <div className="flex justify-between gap-x-2 min-[500px]:justify-normal">
                    <TimeframeTabSelect
                      selected={selectedTimeframe}
                      onChange={setSelectedTimeframe}
                      timeframeTabs={timeframeTabOptions}
                    />
                    <div className="flex">
                      <TabSelect
                        tabs={chartTabs}
                        selectedTab={selectedChartTab}
                        onSelectionChange={(tab) => setSelectedChartTab(tab as ChartTabOption)}
                        pillWidth={60}
                        pillHeight={26}
                        containerMargin={2}
                        btnClasses="text-12"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex h-px bg-cf-gray-4" />
                <div className="flex gap-x-8 p-6">
                  <StatCard
                    className="min-w-fit"
                    dateRange="Current"
                    value={`${currentDelegatedAmount.toFormatted('numeral')} ${FLIP_SYMBOL}`}
                    secondaryValue={formatUsdValue(currentDelegatedValueUsd)}
                    icon={<VolumeIcon />}
                    isLoading={isLoading}
                    title={`Delegated ${FLIP_SYMBOL}`}
                  />
                  <StatCard
                    className="min-w-fit"
                    dateRange={selectedTimeframe.longLabel}
                    value={`${rewards.toFormatted('numeral')} ${FLIP_SYMBOL}`}
                    secondaryValue={formatUsdValue(rewardsValueUsd)}
                    icon={<MoneyBagIcon />}
                    isLoading={isRewardsLoading}
                    title="Rewards"
                  />
                  <StatCard
                    className="min-w-fit"
                    dateRange="Current"
                    value={formatPct(delegatorApy?.type === 'apyPct' ? delegatorApy.apyPct : 0)}
                    icon={<PercentageIcon />}
                    isLoading={isApyLoading}
                    title="APY"
                  />
                </div>
                <div className="p-6">
                  {selectedChartTab.label === 'Balance' ? (
                    <BalanceChart
                      data={data.map((d) => ({
                        date: d.date,
                        balance: TokenAmount.fromAsset(d.balance, 'Flip').toFixed(5),
                      }))}
                      isLoading={isLoading}
                      aggregationInterval="D"
                      exportCsvData={exportCsvData}
                      nonZeroStart
                    />
                  ) : (
                    <RewardsChart
                      data={data.map((d) => ({
                        date: d.date,
                        rewards: TokenAmount.fromAsset(d.rewards, 'Flip').toFixed(5),
                      }))}
                      isLoading={isLoading}
                      aggregationInterval="D"
                      exportCsvData={exportCsvData}
                    />
                  )}
                </div>
              </div>
            )}
            <DelegateFlipCard
              operator={upcomingOperator}
              openModal={openModal}
              newOperator={selectedOperator}
              selectNewOperator={setSelectedOperator}
              closeModal={closeModal}
              modalOpen={open}
              operators={operators}
              newDelegation={false}
            />
          </div>
        ) : (
          <ActivityView goToDelegate={() => setSelectedTab('my-position')} />
        )}
      </div>
    </>
  );
};

export default function Page() {
  const {
    idSs58: delegatorIdSs58,
    currentEpoch,
    nextEpoch,
    isLoading: accountLoading,
  } = useEvmScAccount();
  const { operators = [] } = useOperatorsAndAuction();
  const currentOperator =
    currentEpoch && operators.find((o) => o.idSs58 === currentEpoch.operatorIdSs58);
  const upcomingOperator =
    nextEpoch && operators.find((o) => o.idSs58 === nextEpoch.operatorIdSs58);
  const [queryOperatorId, setQueryOperatorId] = useQueryParam({
    name: 'operator',
    parse: (v) => (isChainflipSs58Address(v) ? v : undefined),
    defaultValue: undefined,
  });
  const [queryParamOperator, setQueryParamOperator] = useState<OperatorSummary>();
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (queryOperatorId) {
      const op = operators.find((o) => o.idSs58 === queryOperatorId);
      if (op) setQueryOperatorId(undefined);
      setQueryParamOperator(op);
    }
  }, [queryOperatorId, operators]);

  const { data: accountHasActivity, isLoading: activityLoading } = useGqlQuery(
    checkForActivityQuery,
    {
      variables: { delegationIdSs58: delegatorIdSs58 as string },
      enabled: isNotNullish(delegatorIdSs58),
      context: { clientName: 'processor' },
      select: (data) => (data?.activity?.totalCount ?? 0) > 0,
      // prevents an automatic redirect after finishing the delegation flow
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  return (
    <Layout title="Delegate">
      {accountLoading || activityLoading ? (
        <div className="flex h-full min-h-screen items-center justify-center">
          <LoadingSpinner className="!h-[40px] !w-[40px]" />
        </div>
      ) : (
        <AnimatePresence mode="wait">
          {(accountHasActivity || completed) && delegatorIdSs58 ? (
            <motion.div className="flex w-full flex-col gap-y-6" {...defaultAnimationProps}>
              <DelegatedView
                delegatorIdSs58={delegatorIdSs58}
                operators={operators}
                currentOperator={currentOperator}
                upcomingOperator={upcomingOperator}
                queryParamOperator={queryParamOperator}
              />
            </motion.div>
          ) : (
            <motion.div className="flex w-full flex-col gap-y-6" {...defaultAnimationProps}>
              <DelegationFlow
                operators={operators}
                queryParamOperator={queryParamOperator}
                onComplete={() => setCompleted(true)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </Layout>
  );
}
